import React, { FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styles from './ResetPassword.module.scss';
import logo from '../../../assets/images/Color_CombatTv_Logo.svg';
import { toast } from 'react-toastify';
import { useUserService } from '../../../services/user.service';
import { ResetPasswordPayload } from '../../../types/user.types';
import { Routes } from '../../../types/constants';
import { openAuthModal } from '../../../utils/auth-modal.events';
import { getSafeInternalReturnTo } from '../../../utils/join';

const passwordComplexityRule = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,256}$/;
const passwordRequirementsMessage =
    'Password requirements: at least 6 characters with 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character.';
const passwordComplexityErrorMessage =
    'Password does not meet requirements. Include uppercase, lowercase, number, and special character.';

const ResetPassword = () => {
    const { resetPassword } = useUserService();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [searchParams] = useSearchParams();

    const token = searchParams.get('token') ?? '';
    const email = searchParams.get('email') ?? '';
    const isResetLinkValid = token.trim().length > 0 && email.trim().length > 0;
    const passwordsMatch = password === confirmPassword;
    const isPasswordComplexityValid = passwordComplexityRule.test(password);

    const submitForm = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setFormError(null);

        if (!passwordsMatch) {
            setFormError('Passwords do not match.');
            return;
        }

        if (!isResetLinkValid) {
            setFormError('This reset link is invalid or expired. Request a new link.');
            return;
        }

        if (!isPasswordComplexityValid) {
            setFormError(passwordComplexityErrorMessage);
            return;
        }

        const payload: ResetPasswordPayload = {
            Email: email,
            ResetToken: token,
            Password: password
        };

        try {
            setIsSubmitting(true);
            const response = await resetPassword(payload);
            if (response.errors) {
                throw new Error(response.errors);
            }

            toast.success('Password reset successfully, redirecting to sign in');
            setTimeout(() => {
                let storedRedirect: string | null = null;
                try {
                    storedRedirect = window.localStorage.getItem('redirectAfterLogin');
                } catch {
                    // Fall back to home when browser storage is unavailable.
                }
                openAuthModal(
                    'sign_in',
                    getSafeInternalReturnTo(storedRedirect, Routes.HOME)
                );
            }, 2000);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '';
            setFormError(errorMessage || 'Failed to reset password. Please request a new link and try again.');
            toast.error('Failed to reset password');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <main className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <span className={styles.heroTag}>Security</span>
                    <h1 className={styles.heroTitle}>Set a new password</h1>
                    <p className={styles.heroSubtitle}>
                        Choose a strong password to finish securing your Combat TV account.
                    </p>
                </div>
                <div className={styles.heroVisual}>
                    <img src={logo} alt="Combat TV logo" className={styles.logo} />
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <h2>Reset password</h2>
                        <p>Use the fields below to complete your password reset.</p>
                    </div>

                    {formError ? <div className={styles.formError}>{formError}</div> : null}

                    {!isResetLinkValid ? (
                        <div className={styles.notice}>
                            <p>This link is missing required reset information.</p>
                            <Link to="/forgot_password" className={styles.linkButton}>
                                Request a new reset link
                            </Link>
                        </div>
                    ) : (
                        <form id="resetPasswordForm" className={styles.form} onSubmit={submitForm}>
                            <div className={styles.fieldGroup}>
                                <label htmlFor="reset-email">Email</label>
                                <input
                                    id="reset-email"
                                    type="email"
                                    value={email}
                                    readOnly
                                    className={styles.readOnlyInput}
                                />
                            </div>

                            <div className={styles.fieldGroup}>
                                <label htmlFor="reset-password">New password</label>
                                <input
                                    id="reset-password"
                                    type="password"
                                    placeholder="Enter your new password"
                                    value={password}
                                    onChange={(event) => {
                                        setPassword(event.target.value);
                                        setFormError(null);
                                    }}
                                    pattern={passwordComplexityRule.source}
                                    title={passwordRequirementsMessage}
                                    required
                                />
                                <p className={styles.helperText}>{passwordRequirementsMessage}</p>
                                {!isPasswordComplexityValid && password.length > 0 ? (
                                    <p className={styles.fieldError}>{passwordComplexityErrorMessage}</p>
                                ) : null}
                            </div>

                            <div className={styles.fieldGroup}>
                                <label htmlFor="reset-password-confirm">Confirm password</label>
                                <input
                                    id="reset-password-confirm"
                                    type="password"
                                    placeholder="Re-enter your new password"
                                    value={confirmPassword}
                                    onChange={(event) => {
                                        setConfirmPassword(event.target.value);
                                        setFormError(null);
                                    }}
                                    required
                                />
                            </div>

                            {!passwordsMatch && confirmPassword.length > 0 ? (
                                <p className={styles.fieldError}>Passwords do not match.</p>
                            ) : null}

                            <div className={styles.actions}>
                                <button
                                    type="submit"
                                    className={styles.primaryButton}
                                    disabled={isSubmitting || !passwordsMatch}
                                >
                                    {isSubmitting ? 'Resetting...' : 'Reset password'}
                                </button>
                                <Link to={Routes.LOGIN} className={styles.secondaryButton}>
                                    Back to sign in
                                </Link>
                            </div>
                        </form>
                    )}
                </div>
            </section>
        </main>
    );
};

export default ResetPassword;
